/**
 * <hs-ask> — PDP "Ask Anything" widget.
 *
 * Drop an empty placeholder on the PDP and this hydrates it:
 *   <div class="hs-ask"
 *        data-product-handle="new-wash-original"
 *        data-product-title="New Wash Original"
 *        data-product-image="https://..."
 *        data-variant-title="8oz" data-price="$48.00"></div>
 *
 * Collapsed: one input + the FAQ chips. Tapping a chip or submitting opens a
 * modal and streams the answer. Conversation, session id and accumulated
 * customer context persist in sessionStorage, so a page reload keeps the thread.
 */
(function () {
  'use strict';

  var API = (window.HS_ASK_ENDPOINT || 'https://hairstory-ai-chat.vercel.app') + '/api/ask';
  var MAX_TURNS = 20;

  /* Same mark the quiz chat uses for its thinking state (ai-profile.svg),
     inlined so the theme doesn't need a second asset upload. */
  var AI_MARK =
    '<svg viewBox="0 0 28 28" width="28" height="28" fill="none" aria-hidden="true"><path d="M28 14.518L25.181 5.76982L18.6171 7.91771V0.805664H9.48648V7.85504L2.82245 5.67235L0 14.4205L6.94731 16.6972L2.83626 22.3994L10.2223 27.8057L14.0535 22.4969L17.8329 27.743L25.2224 22.3333L21.1943 16.746L28 14.518Z" fill="#5EB3E5"/></svg>';
  /* Same send glyph as the quiz chat's .send-button. */
  var ARROW =
    '<svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
    '<path d="M8 15V1M8 1L1 8M8 1L15 8" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* Right chevron on each suggested question — same stroke language as the
     send glyph, pointing into the answer rather than expanding in place. */
  var ARROW_RIGHT =
    '<svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
    '<path d="M1 8H15M15 8L8 1M15 8L8 15" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round"/></svg>';

  /* Wait copy. Deliberately not a fake progress sequence — the request is a
     single grounded generation, so these describe that honestly and then stop
     claiming anything. No phase predicts when it will finish. */
  var STATUS_PHASES = [
    { text: 'Thinking it through' },
    { text: 'Still thinking' },
  ];
  /* 5s, not 2s. Answers average ~4.3s, so a shorter hold meant "Still thinking"
     covered most of a perfectly normal wait and made it read as slow. At 5s the
     escalation only appears when the request really is running long. */
  var STATUS_STEP_MS = 5000;

  var FOOTER =
    '<div class="hs-ask-footer" title="Responses are AI-generated from Hairstory product information. AI can make mistakes.">' +
    'Powered by AI. Trusted by 1,000,000+ customers!' +
    '</div>';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* The server truncates the answer at any leaked schema tag, but the widget
     renders partial stream chunks and shouldn't rely on a single layer for it. */
  var STRUCT_TAG = /<\/?(?:answer|cites|confidence|followups|customer)\b[^>]*>/i;

  function cleanAnswer(text) {
    var s = String(text == null ? '' : text).replace(/^\s*<answer\b[^>]*>/i, '');
    var i = s.search(STRUCT_TAG);
    return i === -1 ? s : s.slice(0, i).replace(/\s+$/, '');
  }

  /** Deliberately tiny markdown subset — bold, links, bullets, paragraphs.
   *  Everything is escaped first, so model output can never inject markup.
   *
   *  Parses line by line rather than requiring a whole blank-line-delimited
   *  block to be uniformly a list. An earlier version did the latter, so the
   *  common "lead-in sentence, then bullets, then a closing line" shape — with
   *  no blank lines between — collapsed into one dense paragraph with literal
   *  "- " characters. The model's structure was being thrown away on render. */
  function md(text) {
    var lines = esc(cleanAnswer(text)).split('\n');

    function inline(s) {
      return s
        .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
          '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    }

    var out = '';
    var para = [];   // consecutive prose lines
    var items = [];  // consecutive bullet lines

    function flushPara() {
      if (!para.length) return;
      out += '<p>' + inline(para.join('<br>')) + '</p>';
      para = [];
    }
    function flushList() {
      if (!items.length) return;
      out += '<ul>' + items.map(function (i) { return '<li>' + inline(i) + '</li>'; }).join('') + '</ul>';
      items = [];
    }

    for (var i = 0; i < lines.length; i++) {
      var line = lines[i];
      var bullet = line.match(/^\s*(?:[-*•]|\d+\.)\s+(.*)$/);
      if (bullet) {
        flushPara();
        items.push(bullet[1]);
      } else if (!line.trim()) {
        flushPara();
        flushList();
      } else {
        flushList();
        para.push(line.trim());
      }
    }
    flushPara();
    flushList();
    return out;
  }

  class HsAsk extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.handle = this.dataset.productHandle || '';
      this.productTitle = this.dataset.productTitle || '';
      this.image = this.dataset.productImage || '';
      this.live = {
        variantTitle: this.dataset.variantTitle || '',
        price: this.dataset.price || '',
        // Subscribe & Save price, when the product offers one and it differs
        // from the one-time price. The PDP leads with this number, so an answer
        // quoting only the one-time price reads as contradicting the page.
        subPrice: this.dataset.subPrice || '',
        available: this.dataset.available !== 'false',
        pageTitle: this.dataset.productTitle || '',
      };

      this.key = 'hs-ask:' + this.handle;
      var saved = this.load();
      this.messages = saved.messages || [];
      this.customer = saved.customer || {};
      this.sessionId = saved.sessionId || null;
      this.faqs = [];
      this.streaming = false;

      this.renderCollapsed();
      this.fetchBootstrap();
    }

    /* ---------------- persistence ---------------- */

    load() {
      try { return JSON.parse(sessionStorage.getItem(this.key) || '{}'); } catch (e) { return {}; }
    }
    save() {
      try {
        sessionStorage.setItem(this.key, JSON.stringify({
          messages: this.messages.slice(-MAX_TURNS * 2),
          customer: this.customer,
          sessionId: this.sessionId,
        }));
      } catch (e) { /* private browsing — degrade to in-memory */ }
    }

    async fetchBootstrap() {
      try {
        var res = await fetch(API + '?handle=' + encodeURIComponent(this.handle));
        // No brief for this product (accessories, kits, the body-care line).
        // Hide rather than offer an input that can only fail — the theme drops
        // the placeholder on every PDP and can't know which are covered.
        if (res.status === 404) { this.hidden = true; this.innerHTML = ''; return; }
        if (!res.ok) return;
        var data = await res.json();
        this.faqs = data.faqs || [];
        if (!this.productTitle) this.productTitle = data.title || '';
        this.renderFaqs();
      } catch (e) { /* widget still works as a plain input */ }
    }

    /* ---------------- collapsed state ---------------- */

    renderCollapsed() {
      this.innerHTML =
        '<div class="hs-ask-widget">' +
          '<div class="hs-ask-label">' + AI_MARK + '<span>Ask about this product</span></div>' +
          '<form class="hs-ask-form">' +
            '<div class="hs-ask-input-wrap">' +
              '<input class="hs-ask-input" placeholder="What would you like to know?" autocomplete="off" maxlength="500" aria-label="Ask a question about this product">' +
              '<button class="hs-ask-send" type="submit" aria-label="Send" disabled>' + ARROW + '</button>' +
            '</div>' +
          '</form>' +
          '<div class="hs-ask-faqs"></div>' +
        '</div>';

      var input = this.querySelector('.hs-ask-input');
      var send = this.querySelector('.hs-ask-send');
      input.addEventListener('input', function () { send.disabled = input.value.trim().length < 2; });
      this.querySelector('.hs-ask-form').addEventListener('submit', (e) => {
        e.preventDefault();
        var v = input.value.trim();
        if (v.length < 2) return;
        input.value = '';
        send.disabled = true;
        this.openModal();
        this.ask(v);
      });

      if (this.messages.length) this.renderResumeHint();
    }

    renderResumeHint() {
      var bar = document.createElement('button');
      bar.type = 'button';
      bar.className = 'hs-ask-resume';
      bar.textContent = 'Continue your conversation →';
      bar.addEventListener('click', () => this.openModal());
      this.querySelector('.hs-ask-widget').appendChild(bar);
    }

    /**
     * Suggested questions render as the quiz chat's quick-start buttons — the
     * same component as the in-conversation follow-up pills. Deliberately not
     * Junip's sparkle-prefixed rows separated by hairlines: this is an existing
     * Hairstory pattern for "tap a suggested prompt", so the PDP entry point,
     * the quiz and the modal all read as one system.
     */
    renderFaqs() {
      var wrap = this.querySelector('.hs-ask-faqs');
      if (!wrap || !this.faqs.length) return;
      wrap.innerHTML = this.faqs
        .map(function (f) {
          return (
            '<button type="button" class="hs-ask-chip" data-faq-id="' + f.id + '">' +
            '<span>' + esc(f.question) + '</span>' + ARROW_RIGHT +
            '</button>'
          );
        })
        .join('');

      wrap.querySelectorAll('.hs-ask-chip').forEach((el) => {
        el.addEventListener('click', () => {
          /* Local lookup only — the id is never sent; the question is. */
          var id = Number(el.dataset.faqId);
          var q = this.faqs.filter(function (f) { return f.id === id; })[0];
          if (!q) return;
          this.openModal();
          /* A suggested question is just a question — same path as typing it. */
          this.ask(q.question);
        });
      });
    }

    /* ---------------- modal ---------------- */

    /**
     * Everything that must track the modal being open: background scroll is
     * locked, and body.hs-ask-open lets CSS hide the floating Gorgias chat
     * button and the Postscript 10%-off tab, which otherwise sit on top of
     * the modal (same treatment the cart drawer gives them).
     */
    setOpenState(open) {
      document.body.style.overflow = open ? 'hidden' : '';
      document.body.classList.toggle('hs-ask-open', open);
    }

    openModal() {
      if (this.modal) { this.modal.hidden = false; this.setOpenState(true); this.focusInput(); return; }

      var m = document.createElement('div');
      m.className = 'hs-ask-modal-wrap';
      m.innerHTML =
        '<div class="hs-ask-backdrop" data-close></div>' +
        '<div class="hs-ask-modal" role="dialog" aria-modal="true" aria-label="Ask about ' + esc(this.productTitle) + '">' +
          '<div class="hs-ask-head">' +
            (this.image ? '<img class="hs-ask-thumb" src="' + esc(this.image) + '" alt="">' : '') +
            '<div class="hs-ask-head-text">' +
              '<span class="hs-ask-head-title">Ask anything</span>' +
              '<span class="hs-ask-head-sub">' + esc(this.productTitle) + '</span>' +
            '</div>' +
            '<button class="hs-ask-restart" type="button" aria-label="Restart chat" title="Restart chat">↺</button>' +
            '<button class="hs-ask-close" type="button" aria-label="Close" data-close>×</button>' +
          '</div>' +
          '<div class="hs-ask-thread" aria-live="polite"></div>' +
          '<div class="hs-ask-foot">' +
            '<form class="hs-ask-form hs-ask-form-modal">' +
              '<div class="hs-ask-input-wrap">' +
                '<input class="hs-ask-input" placeholder="Your reply..." autocomplete="off" maxlength="500" aria-label="Your reply">' +
                '<button class="hs-ask-send" type="submit" aria-label="Send" disabled>' + ARROW + '</button>' +
              '</div>' +
            '</form>' +
          '</div>' +
          // Outside .hs-ask-foot so it can run edge to edge along the bottom of
          // the modal rather than being inset by the input area's padding.
          FOOTER +
        '</div>';

      document.body.appendChild(m);
      this.modal = m;
      this.thread = m.querySelector('.hs-ask-thread');

      m.querySelectorAll('[data-close]').forEach((el) =>
        el.addEventListener('click', () => this.closeModal())
      );
      m.querySelector('.hs-ask-restart').addEventListener('click', () => this.restart());
      this._esc = (e) => { if (e.key === 'Escape') this.closeModal(); };
      document.addEventListener('keydown', this._esc);

      var input = m.querySelector('.hs-ask-input');
      var send = m.querySelector('.hs-ask-send');
      input.addEventListener('input', function () { send.disabled = input.value.trim().length < 2; });
      m.querySelector('.hs-ask-form-modal').addEventListener('submit', (e) => {
        e.preventDefault();
        var v = input.value.trim();
        if (v.length < 2 || this.streaming) return;
        input.value = '';
        send.disabled = true;
        this.ask(v);
      });

      this.setOpenState(true);
      this.replayThread();
      this.focusInput();
    }

    closeModal() {
      if (!this.modal) return;
      this.modal.hidden = true;
      this.setOpenState(false);
      if (this._abort) this._abort.abort();
    }

    /**
     * The modal is portalled to <body>, so it does NOT come away with this
     * element when the host is removed — and on the v8 PDP the host IS removed,
     * every time the shopper changes formula or size, because those are separate
     * products and the snippet rebuilds the placeholder.
     *
     * Without this, switching New Wash Rich -> Deep Clean left the Rich modal
     * sitting open over the Deep Clean page, still captioned "New Wash Rich",
     * with body scroll locked by a component that no longer exists — and a fresh
     * modal leaked into <body> on every subsequent switch.
     */
    disconnectedCallback() {
      this.stopStatus();
      if (this._abort) this._abort.abort();
      if (this._esc) document.removeEventListener('keydown', this._esc);
      if (this.modal) {
        // Only release the open state if this instance was the one holding it.
        if (!this.modal.hidden) this.setOpenState(false);
        this.modal.remove();
        this.modal = null;
      }
    }

    focusInput() {
      var i = this.modal && this.modal.querySelector('.hs-ask-input');
      if (i) setTimeout(function () { i.focus(); }, 50);
    }

    restart() {
      this.messages = [];
      this.customer = {};
      this.sessionId = null;
      this.save();
      if (this.thread) this.thread.innerHTML = '';
      var hint = this.querySelector('.hs-ask-resume');
      if (hint) hint.remove();
    }

    replayThread() {
      if (!this.thread) return;
      this.thread.innerHTML = '';
      this.messages.forEach((m) => {
        if (m.role === 'user') this.addUser(m.content);
        else this.addAssistant(m.content);
      });
      if (this.messages.length) this.placeMark(false);
    }

    /* ---------------- rendering ---------------- */

    addUser(text) {
      var el = document.createElement('div');
      el.className = 'hs-ask-user';
      el.innerHTML = '<div class="hs-ask-bubble">' + esc(text) + '</div>';
      this.thread.appendChild(el);
      this.scroll();
    }

    addAssistant(text) {
      var el = document.createElement('div');
      el.className = 'hs-ask-answer';
      el.innerHTML = '<div class="hs-ask-md">' + md(text || '') + '</div>';
      this.thread.appendChild(el);
      this.scroll();
      return el;
    }

    /**
     * Exactly one mark exists at a time, always the last thing in the thread —
     * which puts it under the newest answer and below its follow-up pills.
     * Spinning while we wait, static once the answer has landed.
     */
    placeMark(spinning) {
      if (!this.thread) return null;
      var old = this.thread.querySelector('.hs-ask-mark');
      if (old) old.remove();
      this.stopStatus();

      var el = document.createElement('div');
      el.className = 'hs-ask-mark' + (spinning ? ' hs-ask-mark-spinning' : '');
      el.innerHTML = AI_MARK;

      if (spinning) {
        el.setAttribute('role', 'status');
        el.setAttribute('aria-label', 'Thinking');
        var label = document.createElement('span');
        label.className = 'hs-ask-status';
        // The rotating copy is decorative — role="status" on the parent already
        // announces "Thinking" once, and re-announcing every phase would talk
        // over a screen reader for the whole wait.
        label.setAttribute('aria-hidden', 'true');
        label.textContent = STATUS_PHASES[0].text;
        el.appendChild(label);
        this.startStatus(label);
      }

      this.thread.appendChild(el);
      this.scroll();
      return el;
    }

    /**
     * Answers take ~4s, and a spinner alone gives no sense of progress. The
     * phases below are honest about what is actually happening — one grounded
     * generation against this product's brief — rather than inventing steps.
     * Nothing here makes the answer arrive sooner; it makes the wait legible.
     */
    startStatus(label) {
      var i = 0;
      var self = this;
      this._statusTimer = setInterval(function () {
        i++;
        if (i >= STATUS_PHASES.length) { clearInterval(self._statusTimer); return; }
        label.textContent = STATUS_PHASES[i].text;
      }, STATUS_STEP_MS);
    }

    stopStatus() {
      if (this._statusTimer) { clearInterval(this._statusTimer); this._statusTimer = null; }
    }

    renderFollowups(list) {
      if (!list || !list.length) return;
      var el = document.createElement('div');
      el.className = 'hs-ask-followups';
      el.innerHTML = list
        .map(function (q) { return '<button type="button" class="hs-ask-chip">' + esc(q) + '</button>'; })
        .join('');
      el.querySelectorAll('.hs-ask-chip').forEach((btn) =>
        btn.addEventListener('click', () => {
          if (this.streaming) return;
          el.remove();
          this.ask(btn.textContent, null);
        })
      );
      this.thread.appendChild(el);
      this.scroll();
    }

    scroll() {
      if (this.thread) this.thread.scrollTop = this.thread.scrollHeight;
    }

    /** Abortable delay — resolves early if the request is cancelled, so
     *  closing the modal mid-hold doesn't leave a timer running. */
    pause(ms) {
      var signal = this._abort && this._abort.signal;
      return new Promise(function (resolve) {
        var t = setTimeout(done, ms);
        function done() {
          clearTimeout(t);
          if (signal) signal.removeEventListener('abort', done);
          resolve();
        }
        if (signal) signal.addEventListener('abort', done);
      });
    }

    /* No typewriter reveal. A first attempt paced the finished text over ~500ms
       with setInterval and left answers truncated when timers were throttled —
       the same timing-dependent render path that dropping streaming removed.
       The answer is complete when it arrives, so render it complete. The stage
       indicator covers the wait. */

    /* ---------------- the ask ---------------- */

    async ask(question) {
      if (this.streaming) return;
      this.streaming = true;

      this.addUser(question);
      this.thread.querySelectorAll('.hs-ask-followups').forEach(function (n) { n.remove(); });
      this.placeMark(true);

      var history = this.messages.slice(-MAX_TURNS * 2);
      this.messages.push({ role: 'user', content: question });

      this._abort = new AbortController();
      var answerEl = null;
      var buffer = '';

      try {
        var res = await fetch(API, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: this._abort.signal,
          body: JSON.stringify({
            handle: this.handle,
            question: question,
            messages: history,
            customerContext: this.customer,
            live: this.live,
            sessionId: this.sessionId,
          }),
        });

        // A 429 body carries a real, customer-readable message. Everything else
        // non-OK is a genuine failure and belongs in the catch.
        if (res.status === 429) {
          var limited = await res.json().catch(function () { return {}; });
          this.addAssistant(limited.answer || "You're asking faster than I can keep up — give me a moment and try again.");
          this.messages.pop();
          return;
        }
        if (!res.ok) throw new Error('HTTP ' + res.status);
        var data = await res.json();


        if (data.error || !data.answer) {
          this.addAssistant(data.error || "Sorry — I couldn't work that one out. Mind rephrasing?");
          this.messages.pop();
          return;
        }

        if (data.sessionId) this.sessionId = data.sessionId;
        if (data.customer) this.customer = Object.assign({}, this.customer, data.customer);

        buffer = data.answer;
        answerEl = this.addAssistant(buffer);
        answerEl.dataset.confidence = data.confidence || '';

        this.messages.push({ role: 'assistant', content: buffer });
        this.save();
        this.renderFollowups(data.followups);
      } catch (err) {
        if (err && err.name !== 'AbortError') {
          this.addAssistant("Sorry — I couldn't reach the server. Mind trying again?");
        }
        this.messages.pop();
      } finally {
        // Re-place last so the mark ends up under the answer AND its pills,
        // whichever path we took (answer, error, abort).
        this.placeMark(false);
        this.streaming = false;
        this._abort = null;
        var input = this.modal && this.modal.querySelector('.hs-ask-input');
        if (input) input.focus();
      }
    }
  }

  if (!customElements.get('hs-ask')) customElements.define('hs-ask', HsAsk);

  /** Hydrate plain <div class="hs-ask"> placeholders (the Junip embed pattern). */
  function hydrate() {
    document.querySelectorAll('div.hs-ask:not([data-hs-ask-init])').forEach(function (div) {
      div.setAttribute('data-hs-ask-init', 'true');
      var el = document.createElement('hs-ask');
      Array.prototype.forEach.call(div.attributes, function (a) {
        if (a.name.indexOf('data-') === 0) el.setAttribute(a.name, a.value);
      });
      div.appendChild(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrate);
  } else {
    hydrate();
  }
  window.HsAskHydrate = hydrate;
})();
